import { useState, useEffect, useRef } from 'react';
import TemplateRequestModal from './TemplateRequestModal';
import type { TemplateItem } from './BrutalGrid';
import ScrollReveal from '@/components/feature/ScrollReveal';

// ─── Floating word cycler ────────────────────────────────────────────────────
const WORDS = ['DESIGN', 'BRAND', 'LAUNCH', 'GROW', 'CONVERT', 'STAND OUT', 'DOMINATE', 'ELEVATE'];

function FloatingWords() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive(prev => (prev + 1) % WORDS.length), 1800);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative h-10 overflow-hidden flex items-center">
      {WORDS.map((word, i) => (
        <span
          key={word}
          className="absolute left-0 text-orange-500 font-black text-3xl md:text-4xl tracking-tight transition-all duration-500"
          style={{
            opacity: i === active ? 1 : 0,
            transform: i === active ? 'translateY(0)' : i < active ? 'translateY(-100%)' : 'translateY(100%)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {word}
        </span>
      ))}
    </div>
  );
}

// ─── Animated thumbnail grid ─────────────────────────────────────────────────
const THUMBS = [
  'https://framerusercontent.com/images/e6ULDXMhILZCRqYzZOtoPJubCc.jpg',
  'https://framerusercontent.com/images/c0VWATPD5g0wT1E5YC1WqWVVILU.jpg',
  'https://framerusercontent.com/images/T9M0du8UsfRz2SkbU6lRldJwNpo.jpg',
  'https://framerusercontent.com/images/83JEHDVVEQAkjP06IedBvGYBoMQ.jpg',
  'https://framerusercontent.com/images/ZASZrm2Fw9By3Qs9Fr0zjMp8.jpg',
  'https://framerusercontent.com/images/zzgoMOtxNaR7UN3mk2rvvlaIfg.jpg',
  'https://framerusercontent.com/images/cTICgYKnabvsGNq4NzM3wybiyM.jpg',
  'https://framerusercontent.com/images/vwi0dMlnXI4rRixNYrBJDN8iw.jpg',
  'https://framerusercontent.com/images/DAbTwqeO5EgJ0TBK8DXFBQt6N5E.jpg',
];

function AnimatedThumbnailGrid() {
  const [highlighted, setHighlighted] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHighlighted(prev => (prev + 1) % THUMBS.length), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="grid grid-cols-3 gap-2 w-full h-full">
      {THUMBS.map((src, i) => (
        <div
          key={src}
          className="relative overflow-hidden rounded-lg transition-all duration-500"
          style={{
            aspectRatio: '4/3',
            transform: i === highlighted ? 'scale(1.04)' : 'scale(1)',
            zIndex: i === highlighted ? 2 : 1,
            boxShadow: i === highlighted ? '0 8px 32px rgba(0,0,0,0.18)' : 'none',
          }}
        >
          <img src={src} alt="" className="w-full h-full object-cover object-top" />
          <div
            className="absolute inset-0 rounded-lg border-2 transition-all duration-500"
            style={{
              borderColor: i === highlighted ? '#ea580c' : 'transparent',
              opacity: i === highlighted ? 1 : 0,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Counting number hook ────────────────────────────────────────────────────
function useCountUp(target: number, duration: number, started: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(id);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(id);
  }, [started, target, duration]);
  return count;
}

// ─── Marquee stat item ───────────────────────────────────────────────────────
interface StatItem {
  prefix?: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

const STATS: StatItem[] = [
  { prefix: '+', value: 600, suffix: '', label: 'Websites Built', icon: 'ri-global-line' },
  { prefix: '', value: 5, suffix: '★', label: 'Google Rating', icon: 'ri-star-fill' },
  { prefix: '', value: 24, suffix: 'hr', label: 'Support', icon: 'ri-customer-service-2-line' },
  { prefix: '+', value: 600, suffix: '', label: 'Websites Built', icon: 'ri-global-line' },
  { prefix: '', value: 5, suffix: '★', label: 'Google Rating', icon: 'ri-star-fill' },
  { prefix: '', value: 24, suffix: 'hr', label: 'Support', icon: 'ri-customer-service-2-line' },
  { prefix: '+', value: 600, suffix: '', label: 'Websites Built', icon: 'ri-global-line' },
  { prefix: '', value: 5, suffix: '★', label: 'Google Rating', icon: 'ri-star-fill' },
  { prefix: '', value: 24, suffix: 'hr', label: 'Support', icon: 'ri-customer-service-2-line' },
];

function StatCard({ stat, started }: { stat: StatItem; started: boolean }) {
  const count = useCountUp(stat.value, 1400, started);
  return (
    <div className="flex items-center gap-4 px-8 py-3 flex-shrink-0">
      <div className="flex flex-col leading-tight">
        <span
          className="font-black text-black leading-none"
          style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.1rem', letterSpacing: '-0.03em' }}
        >
          {stat.prefix}{count}{stat.suffix}
        </span>
        <span className="text-xs text-black/40 font-medium whitespace-nowrap">{stat.label}</span>
      </div>
      {/* Divider slash */}
      <span className="text-black/20 text-xl font-light select-none">/</span>
    </div>
  );
}

// ─── Scrolling marquee banner ────────────────────────────────────────────────
function StatsBanner() {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  // Trigger count-up when banner enters viewport
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={bannerRef}
      className="w-full overflow-hidden border-y border-black/8 bg-white"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div
        className="flex"
        style={{
          animation: 'marquee-scroll 18s linear infinite',
          width: 'max-content',
        }}
      >
        {STATS.map((stat, i) => (
          <StatCard key={i} stat={stat} started={started} />
        ))}
      </div>

      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </div>
  );
}

// ─── Empty template for generic modal ───────────────────────────────────────
const EMPTY_TEMPLATE: TemplateItem = {
  id: 0,
  name: 'Build My Website',
  category: 'Custom',
  tag: null,
  type: 'video',
  price: 15,
  priceId: 'template_standard',
  poster: '',
  video: '',
  externalUrl: '',
};

// ─── Main export ─────────────────────────────────────────────────────────────
export default function BrutalCTABanner() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      {/* Stats marquee */}
      <StatsBanner />

      {/* CTA card */}
      <div className="max-w-[1400px] mx-auto px-5 pt-6 pb-2">
        <ScrollReveal direction="up" duration={750} delay={0} distance={36}>
          <div
            className="w-full rounded-2xl overflow-hidden border border-black/8"
            style={{ background: '#0a0a0a' }}
          >
            <div className="flex flex-col md:flex-row min-h-[260px] md:min-h-[300px]">

              {/* LEFT — CTA */}
              <div className="flex flex-col justify-between p-7 md:p-10 flex-1">
                <span className="text-xs font-bold text-white/30 uppercase tracking-widest">Nova AI Digital</span>

                <div className="flex flex-col gap-2 my-6">
                  <p className="text-white/50 text-sm font-medium uppercase tracking-widest">We help you</p>
                  <FloatingWords />
                  <h2
                    className="text-white font-black leading-none"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 'clamp(28px, 4vw, 48px)',
                      letterSpacing: '-0.03em',
                    }}
                  >
                    YOUR WEBSITE
                  </h2>
                  <p className="text-white/40 text-sm leading-relaxed max-w-xs mt-1">
                    Pick a style you love from our gallery — we'll build your entire site around it.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setModalOpen(true)}
                    className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    BUILD ME A WEBSITE
                  </button>
                  <a
                    href="tel:+19512909947"
                    className="px-6 py-3 border border-white/20 hover:border-white/50 text-white text-sm font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-phone-line"></i>
                    Call Us
                  </a>
                </div>
              </div>

              {/* RIGHT — animated thumbnail grid (desktop only) */}
              <div className="hidden md:flex items-center justify-center p-6 w-[420px] flex-shrink-0">
                <AnimatedThumbnailGrid />
              </div>

              {/* Mobile thumbnail strip */}
              <div className="md:hidden flex gap-2 px-7 pb-6 overflow-x-auto">
                {THUMBS.slice(0, 4).map((src) => (
                  <div key={src} className="flex-shrink-0 w-24 h-16 rounded-lg overflow-hidden">
                    <img src={src} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                ))}
              </div>

            </div>
          </div>
        </ScrollReveal>
      </div>

      <TemplateRequestModal
        template={modalOpen ? EMPTY_TEMPLATE : null}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
